import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const MAX_ATTEMPTS = 6;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking, success, failed, timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= MAX_ATTEMPTS) { if (!cancelled) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 max-w-md w-full text-center" data-testid="payment-return">
        {state === "checking" && (<>
          <Loader2 className="mx-auto h-14 w-14 text-blue-700 animate-spin" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Verifica del pagamento…</h1>
          <p className="text-slate-500 mt-2 text-sm">Attendere, stiamo confermando la transazione.</p>
        </>)}
        {state === "success" && (<>
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" data-testid="payment-success-icon" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Pagamento riuscito</h1>
          <p className="text-slate-500 mt-2 text-sm">Grazie. La fattura è stata saldata correttamente.</p>
          <Button onClick={() => navigate("/invoices")} className="mt-6 bg-blue-900 hover:bg-blue-800 w-full">Vai alle fatture</Button>
        </>)}
        {(state === "failed" || state === "timeout") && (<>
          <XCircle className="mx-auto h-14 w-14 text-red-500" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">{state === "timeout" ? "Verifica in corso" : "Pagamento non completato"}</h1>
          <p className="text-slate-500 mt-2 text-sm">{state === "timeout" ? "Controlla lo stato della fattura tra qualche istante." : "La transazione non è stata completata."}</p>
          <Button onClick={() => navigate("/invoices")} variant="outline" className="mt-6 w-full">Torna alle fatture</Button>
        </>)}
      </div>
    </div>
  );
}
